import { cn } from '@/lib/utils';

interface BrandMarkProps {
  /** Total height of the mark in px; the SVG scales width proportionally.
   *  The wordmark, when shown, sizes proportionally to this value. Default 22. */
  size?: number;
  /** Render the wordmark "LeanOpx" next to the mark. Default true. */
  showWordmark?: boolean;
  /**
   * Explicit aria-label for the SVG. When omitted the SVG is **always
   * decorative** (`aria-hidden`) — including in icon-only contexts —
   * because most call sites have an adjacent visible "LeanOpx" string
   * (e.g. the sidebar header places `<BrandMark showWordmark={false}>`
   * next to a `.side-label-text` span). Double-announcement was the
   * default failure mode in v5 (review on PR #115).
   *
   * Set this when the mark is the ONLY brand affordance on the screen
   * (e.g. a favicon-sized placement with no adjacent text) — pass a
   * concise string like `"LeanOpx"`. The component then opts into
   * `role="img"` + `aria-label`.
   */
  label?: string;
  className?: string;
}

/**
 * The LeanOpx brand mark — v8 geometry per the designer handoff
 * (`/tmp/leanopx-design--NVURoc16TJeOLvm23XVag/leanopx/project/assets/`
 *  `marketing.jsx` L25–29, `capture.jsx` L225–228, sidebar variant in
 *  `assets/e2d10079-9cdf-4d4a-bdfb-5a1b9a106056.js` L176–180):
 *
 *   <svg viewBox="0 0 28 28" fill="none" strokeWidth="1.4">
 *     <circle r=12 stroke="var(--color-ink-faint)" />
 *     <circle r=7  stroke="var(--color-ink-muted)" />
 *     <circle r=2.5 fill="var(--color-accent)" />
 *   </svg>
 *
 * Two concentric ink rings closing in on an accent-coloured focal dot —
 * "what the agent watches": a lens (an aperture), a focused center,
 * rings of context. Ring strokes are explicit ink tokens (`ink-faint`
 * outer, `ink-muted` inner) so the mark reads consistently across
 * daylight/midnight themes and on accent-tinted surfaces; the focal
 * dot locks to `--color-accent`. The previous opacity-on-currentColor
 * approach drifted on tinted backgrounds — explicit tokens are the
 * design's intent.
 *
 * Geometry follows the v8 sidebar-rail variant (radii 12 / 7 / 2.5,
 * stroke 1.4) since the component's viewBox is 28 — the marketing
 * 32-viewBox variant uses 14 / 8 / 2.5 with an extra outer ring at 14;
 * we deliberately stay at two rings for the sidebar proportions.
 *
 * When `showWordmark` is true (default), the component renders the mark
 * + "LeanOpx" wordmark as a single inline flex group — the typical
 * sidebar/auth-page treatment. Set `false` for icon-only contexts
 * (favicon-style placements, dense chrome).
 */
export function BrandMark({ size = 22, showWordmark = true, label, className }: BrandMarkProps) {
  // Wordmark sizes from the designer reference: at size=22 the wordmark
  // sits at ~14px (sidebar header / nav), at size=64 it scales up
  // proportionally for the sign-in marquee. Using a linear ratio keeps
  // the optical balance consistent across both ends.
  const wordmarkPx = Math.round(size * 0.64);

  // Decorative by default: callers without an explicit `label` get an
  // `aria-hidden` SVG, even in icon-only contexts. The expectation is
  // that adjacent visible text (the wordmark when `showWordmark=true`,
  // or a sibling brand string in the surrounding markup) provides the
  // accessible name. Opt into role="img" + aria-label only when the
  // mark is the screen's sole brand affordance.
  const decorative = !label;

  return (
    <span className={cn('inline-flex items-center gap-2 text-[var(--color-ink)]', className)}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 28 28"
        width={size}
        height={size}
        fill="none"
        strokeWidth="1.4"
        {...(decorative
          ? { 'aria-hidden': true, focusable: false }
          : { role: 'img', 'aria-label': label })}
        className="inline-block shrink-0"
      >
        <circle cx="14" cy="14" r="12" stroke="var(--color-ink-faint)" />
        <circle cx="14" cy="14" r="7" stroke="var(--color-ink-muted)" />
        <circle cx="14" cy="14" r="2.5" fill="var(--color-accent)" />
      </svg>
      {showWordmark && (
        <span
          className="font-medium tracking-[-0.01em] text-[var(--color-ink)]"
          style={{ fontSize: `${wordmarkPx}px`, lineHeight: 1 }}
        >
          LeanOpx
        </span>
      )}
    </span>
  );
}
